<template>
    <modal :show="show" v-on:update:show="handleModalClose">
        <template slot="header">
            <h5 class="modal-title">{{ sipdevice }}'s settings</h5>
        </template>

        <template>
            <div v-if="loadingSettings" class="text-center title">
                <font-awesome-icon :icon="['fal','spinner-third']" spin></font-awesome-icon>
            </div>

            <form v-if="!loadingSettings">
                <div class="row">
                    <div class="col">
                        <base-input label="Name"
                                    type="text"
                                    placeholder="Enter name"
                                    v-model="deviceSettings.name"
                                    name="name"
                                    required
                        >
                            <small slot="helperText" id="nameHelp" class="form-text text-muted">This will be used
                                throughout
                                the
                                portal to identify this device.
                            </small>
                        </base-input>
                    </div>

                </div>

                <div class="row">
                    <div class="col-4">
                        <base-input label="Caller ID Number">
                            <select
                                id="calleridselect"
                                placeholder=""
                                v-model="deviceSettings.callerid_E164"
                                class="form-control"
                                size="small"
                                name="callerid">
                                <option class="select-primary"
                                        label="No CallerID"
                                        :value="null">

                                </option>
                                <option v-for="number in uniqueDIDs"
                                        class="select-primary"
                                        :label="number.tag != null ? number.tag + ' (' + number.formatted_did + ')' : number.formatted_did"
                                        :key="number.formatted_did"
                                        :value="number.formatted_did">
                                </option>
                            </select>
                        </base-input>
                    </div>
                    <div class="col-8">
                        <base-input label="Caller ID Name"
                                    type="text"
                                    class="col"
                                    placeholder="Enter name"
                                    v-model="deviceSettings.callerid_name"
                                    name="callerid_name"
                                    :error="getError('callerid_name')"
                        >
                            <small slot="helperText" id="callerid_name_help" class="form-text text-muted">
                                This name will show up on internal calls.
                            </small>
                        </base-input>
                    </div>

                </div>
                <div class="row">
                    <div class="col">
                        <voicemail-selector class="mb-4 mt-1"
                                            :initial-voicemail="deviceSettings.voicemail"
                                            v-on:update="handleVoicemail($event)"></voicemail-selector>
                    </div>

                </div>
                <div class="row">
                    <div class="col">
                        <music-on-hold-selector v-model="deviceSettings.musiconhold"
                                                @change="dirty = true"></music-on-hold-selector>

                    </div>

                </div>

                <div class="form-row">
                    <div class="col-lg-4">
                        <p class="category">Enable Call Recording?</p>
                        <base-switch v-model="isRecordingEnabled" on-text="ON" off-text="OFF"></base-switch>
                    </div>
                    <div v-if="isRecordingEnabled" class="form-group col-lg-8">
                        <base-input label="Recording Email"
                                    type="email"
                                    class="animated"
                                    v-bind:class="{fadeInRight: isRecordingEnabled}"
                                    placeholder="Enter email"
                                    required
                                    name="email"
                                    :error="getError('email')"
                                    v-validate="'required|email'"
                                    v-model="deviceSettings.recordingemail"
                        >
                            <small slot="helperText" id="emailHelp" class="form-text text-muted">Call recordings will be
                                sent to this address.
                            </small>
                            <small slot="helperText" id="callrecordingHelp" class="form-text text-muted">A monthly
                                charge of 2 {{ this.$store.getters['customer/currency'] }} will be added to your account
                            </small>
                        </base-input>
                    </div>

                </div>
            </form>
        </template>


        <template slot="footer" v-if="!loadingSettings">
            <base-button type="primary" native-type="submit" @click.prevent="validate"
                         :loading="saving"> Save changes
            </base-button>
            <base-button type="secondary" @click.native="$emit('update:show', false)">Close</base-button>
        </template>
    </modal>
</template>

<script>
    import Modal from "../../components/Modal";
    import {mapFields} from 'vuex-map-fields';
    import BaseSwitch from "../../components/BaseSwitch";
    import VueTelInput from 'vue-tel-input'
    import MusicOnHoldSelector from "../../components/MusicOnHold/MusicOnHoldSelector";
    import VoicemailSelector from "../Voicemail/VoicemailSelector";

    export default {
        name: "SIPDeviceSettingsModal",
        components: {
            VoicemailSelector,
            MusicOnHoldSelector,
            Modal,
            BaseSwitch,
            VueTelInput,
        },
        props: {
            sipdevice: String,
            show: Boolean,
        },
        data() {
            return {
                isRecordingEnabled: false,
                deviceSettings: {},
                saving: false,
            }
        },
        computed: {
            ...mapFields(`sipdevice`, ['sipdeviceSettings', 'loadingSettings']),
            ...mapFields(`voicemail`, ['voicemails']),
            ...mapFields(`musiconhold`, ['musiconholds']),
            ...mapFields(`did`, ['uniqueDIDs']),
        },
        methods: {
            getError(fieldName) {
                return this.errors.first(fieldName)
            },
            validate() {
                this.$validator.validateAll().then(isValid => {
                    if (isValid) {
                        this.updateSettings()
                    }
                })
            },
            handleModalClose(value) {
                this.$emit('update:show', value);
            },
            handleVoicemail(selectedVoicemail) {
                this.$set(this.deviceSettings, 'voicemail', selectedVoicemail.name)
            },
            updateSettings() {
                this.saving = true;
                this.sipdeviceSettings[this.sipdevice] = this.deviceSettings;
                this.$store.dispatch('sipdevice/saveSettings', {
                    sipdevice: this.sipdevice,
                    settings: this.deviceSettings
                }).then(() => {
                    if (this.$store.getters['sipdevice/needUpdate']) {
                        this.$store.dispatch('sipdevice/loadSIPDevices');
                    }
                    this.saving = false;
                    this.$emit('update:show', false);
                    this.$notify({
                        position: "top-right",
                        message: this.sipdevice + '\'s settings saved!',
                        type: "success",
                        icon: "fal fa-save",
                        title: "Saved!"
                    });
                }).catch(() => {
                    this.saving = false;
                    this.$notify({
                        position: "bottom-right",
                        message: this.sipdevice + '\'s settings failed to save!',
                        type: "error",
                        icon: "fal fa-times",
                        title: "Error!"
                    });
                });
            },
        },
        mounted() {
            this.isRecordingEnabled = (this.deviceSettings.recordingemail != null);
            this.deviceSettings.name = null;
        },
        watch: {
            // whenever sipdevice changes, this function will run
            sipdevice: function (newSipDevice, oldSipDevice) {
                if (this.sipdevice != null) {
                    this.$store.dispatch('sipdevice/loadSettings', {sipdevice: newSipDevice}).then(() => {
                        this.deviceSettings = this.sipdeviceSettings[this.sipdevice];
                        this.isRecordingEnabled = (this.deviceSettings.recordingemail != null);
                        // this.$emit('update:show', true)

                    }).catch(() => {
                        this.$notify({
                            position: "bottom-right",
                            message: 'Failed to retrieve the settings! Please try again later!',
                            type: "error",
                            icon: "fal fa-times",
                            title: "Error!"
                        });
                        this.handleModalClose(false);
                    });
                }
            },
            isRecordingEnabled: function (newValue, oldValue) {
                console.log(newValue);
                if (newValue === false) {
                    this.deviceSettings.recordingemail = null;
                }
            }
        },
    }
</script>

<style scoped>

</style>
